/**
 * Turns react-easy-crop's pixelCrop (the square the rep chose, in the
 * source image's own pixel coordinates) into an actual cropped JPEG File,
 * client side, before anything reaches the upload pipeline (task: headshot
 * crop tool, 2026-09-15).
 *
 * Deliberately returns a File, not a bare Blob: worker/lib/images.ts's
 * readUploadedHeadshot requires `file instanceof File`, and relying on a
 * browser's implicit Blob-to-File wrapping when it is appended to
 * FormData is not worth trusting over just constructing one directly.
 *
 * No EXIF orientation to worry about here: this is a fresh canvas render,
 * carrying no EXIF of its own, of pixels the browser already decoded and
 * displayed in the visually correct orientation (canvas drawImage honors a
 * source image's EXIF orientation the same way an <img> tag does, in every
 * browser this app targets). honorExifOrientation: false on the upload
 * tells the server to trust that rather than re-applying a rotation.
 */
export async function cropImageToFile(imageSrc, pixelCrop, fileName = 'headshot.jpg') {
  const image = await loadImage(imageSrc)

  const canvas = document.createElement('canvas')
  canvas.width = Math.round(pixelCrop.width)
  canvas.height = Math.round(pixelCrop.height)
  const ctx = canvas.getContext('2d')
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    canvas.width,
    canvas.height,
  )

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((result) => (result ? resolve(result) : reject(new Error('Could not crop that photo.'))), 'image/jpeg', 0.92)
  })

  return new File([blob], fileName, { type: 'image/jpeg' })
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Could not load that photo.'))
    img.src = src
  })
}
